import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { STATUS_LABELS, SHIPMENT_STATUS_LABELS } from '../../lib/constants';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import { CountryRoute } from '../../components/CountryDisplay';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Ship, Package, Calendar, MapPin, AlertCircle, Clock, Search, ChevronDown, ChevronUp, Weight, Plane } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';

const HISTORY_STATUSES = ['completed', 'cancelled'];

const PartnerShipmentsView = () => {
  const { t, language } = useLanguage();
  const [myPackages, setMyPackages] = useState([]);
  const [shipmentsWithPackages, setShipmentsWithPackages] = useState([]);
  const [scheduledShipments, setScheduledShipments] = useState([]);
  const [historyShipments, setHistoryShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedShipments, setExpandedShipments] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, shipmentsRes] = await Promise.all([
        api.get('/api/packages?include_collected=true'),
        api.get('/api/shipments'),
      ]);

      const allPackages = packagesRes.data;
      setMyPackages(allPackages);

      const allShipments = shipmentsRes.data;

      // Group packages by shipment
      const packagesWithShipment = allPackages.filter(p => p.shipment_id);
      const myShipmentIds = new Set(packagesWithShipment.map(p => p.shipment_id));

      // Shipments that contain my packages
      const myShipments = allShipments
        .filter(s => myShipmentIds.has(s.id))
        .map(shipment => ({
          ...shipment,
          packages: packagesWithShipment.filter(p => p.shipment_id === shipment.id)
        }));

      // Fully-delivered or cancelled departures move to the "Historique" tab
      setShipmentsWithPackages(myShipments.filter(s => !HISTORY_STATUSES.includes(s.status)));
      setHistoryShipments(myShipments.filter(s => HISTORY_STATUSES.includes(s.status)));

      // Scheduled/planned shipments (no packages from this partner yet).
      // activeStatuses intentionally excludes HISTORY_STATUSES (completed/cancelled).
      const activeStatuses = ['planned', 'scheduled', 'in_progress'];
      const scheduled = allShipments.filter(
        s => activeStatuses.includes(s.status) && !myShipmentIds.has(s.id)
      );
      setScheduledShipments(scheduled);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(language === 'fr' ? 'Erreur lors du chargement des données' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-muted-foreground">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
        </div>
      </ProfessionalLayout>
    );
  }

  const pendingPackages = myPackages.filter(p => !p.shipment_id);
  const totalCollected = myPackages.filter(p => p.shipment_id).length;

  const isOldPackage = (pkg) => {
    if (pkg.shipment_id) return false;
    const created = new Date(pkg.created_at);
    const now = new Date();
    return (now - created) / (1000 * 60 * 60 * 24) > 7;
  };

  const toggleShipment = (shipmentId) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedShipments(newExpanded);
  };

  const filterData = (items, query) => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(item => {
      if (item.name) {
        return (
          item.name.toLowerCase().includes(q) ||
          (item.origin_country || '').toLowerCase().includes(q) ||
          (item.destination_country || '').toLowerCase().includes(q) ||
          (item.status || '').toLowerCase().includes(q) ||
          (item.packages || []).some(p =>
            (p.oce_code || '').toLowerCase().includes(q) ||
            (p.sender_name || '').toLowerCase().includes(q) ||
            (p.recipient_name || '').toLowerCase().includes(q)
          )
        );
      }
      return (
        (item.oce_code || '').toLowerCase().includes(q) ||
        (item.sender_name || '').toLowerCase().includes(q) ||
        (item.recipient_name || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    });
  };

  const filteredShipments = filterData(shipmentsWithPackages, searchQuery);
  const filteredScheduled = filterData(scheduledShipments, searchQuery);
  const filteredPending = filterData(pendingPackages, searchQuery);
  const filteredHistory = filterData(historyShipments, searchQuery);

  const statusLabels = SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'arrived': return 'bg-green-500';
      case 'in_progress': case 'in_transit': return 'bg-blue-500';
      case 'scheduled': case 'planned': return 'bg-amber-500';
      case 'cancelled': return 'bg-red-500';
      case 'delayed': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const renderShipmentCard = (shipment) => {
    const isExpanded = expandedShipments.has(shipment.id);
    const totalWeight = shipment.packages.reduce((sum, p) => sum + (p.weight || 0), 0);
    return (
      <Card key={shipment.id} className="hover:shadow-md transition-shadow">
        <CardHeader className="cursor-pointer" onClick={() => toggleShipment(shipment.id)}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Ship className="h-5 w-5 text-accent" />
                <div>
                  <CardTitle className="text-xl">{shipment.name}</CardTitle>
                  <CardDescription className="mt-1 flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                    </span>
                    <span className="flex items-center gap-1 font-semibold text-accent">
                      <Weight className="h-3.5 w-3.5" />
                      {totalWeight.toFixed(1)} kg
                    </span>
                    <span className="text-muted-foreground">
                      {shipment.packages.length} {language === 'fr' ? 'colis' : 'packages'}
                    </span>
                    {shipment.departure_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(shipment.departure_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                      </span>
                    )}
                  </CardDescription>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(shipment.status)}>
                {statusLabels[shipment.status] || shipment.status}
              </Badge>
              {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent>
            {shipment.notes && (
              <div className="mb-4 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                <p className="text-sm text-accent flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{shipment.notes}</span>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-foreground mb-3">
                {language === 'fr' ? `Mes colis dans ce départ (${shipment.packages.length})` : `My packages in this departure (${shipment.packages.length})`}
              </h4>
              {shipment.packages.map(pkg => (
                <div key={pkg.id} className="p-3 border rounded-lg hover:bg-muted/30">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-accent">{pkg.oce_code}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {pkg.sender_name} → {pkg.recipient_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {pkg.weight} kg | {pkg.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={pkg.is_paid ? 'bg-green-100 text-green-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>
                          {pkg.is_paid
                            ? (language === 'fr' ? 'Réglé' : 'Paid')
                            : (language === 'fr' ? 'Non réglé' : 'Unpaid')}
                        </Badge>
                      </div>
                    </div>
                    <PackageStatusBadge status={pkg.status} size="sm" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
              <p>{language === 'fr' ? 'Créé le' : 'Created'}: {new Date(shipment.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}</p>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <Ship size={24} className="text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold">{language === 'fr' ? 'Départs' : 'Departures'}</h1>
            <p className="text-muted-foreground text-sm">
              {language === 'fr' ? 'Suivi des départs et de vos colis' : 'Track departures and your packages'}
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'fr' ? 'Total Colis' : 'Total Packages'}</CardTitle>
              <Package className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{myPackages.length}</div>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderColor: '#f0d88a' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'fr' ? 'En Attente' : 'Pending'}</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingPackages.length}</div>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'fr' ? 'Poids Total' : 'Total Weight'}</CardTitle>
              <Weight className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {myPackages.reduce((sum, p) => sum + (p.weight || 0), 0).toFixed(1)} kg
              </div>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0e4ff 100%)', borderColor: '#d4b8f0' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'fr' ? 'Mes Départs' : 'My Departures'}</CardTitle>
              <Ship className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{shipmentsWithPackages.length}</div>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'fr' ? 'Programmés' : 'Scheduled'}</CardTitle>
              <Plane className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scheduledShipments.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={language === 'fr' ? 'Rechercher par départ, code OCE, client, statut...' : 'Search by departure, OCE code, client, status...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="shipments" className="w-full">
          <TabsList>
            <TabsTrigger value="shipments">
              {language === 'fr' ? 'Mes Départs' : 'My Departures'} ({shipmentsWithPackages.length})
            </TabsTrigger>
            <TabsTrigger value="scheduled">
              {language === 'fr' ? 'Départs Programmés' : 'Scheduled Departures'} ({scheduledShipments.length})
            </TabsTrigger>
            <TabsTrigger value="pending">
              {language === 'fr' ? 'Colis Non Collectés' : 'Uncollected Packages'} ({pendingPackages.length})
            </TabsTrigger>
            <TabsTrigger value="history">
              {language === 'fr' ? 'Historique' : 'History'} ({historyShipments.length})
            </TabsTrigger>
          </TabsList>

          {/* Mes Départs — shipments with my packages */}
          <TabsContent value="shipments" className="space-y-4">
            {filteredShipments.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Ship className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {searchQuery
                        ? (language === 'fr' ? 'Aucun résultat' : 'No results')
                        : (language === 'fr' ? 'Aucun départ avec vos colis' : 'No departures with your packages')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredShipments.map(renderShipmentCard)
            )}
          </TabsContent>

          {/* Départs Programmés — upcoming shipments without my packages */}
          <TabsContent value="scheduled" className="space-y-4">
            {filteredScheduled.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Plane className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {searchQuery
                        ? (language === 'fr' ? 'Aucun résultat' : 'No results')
                        : (language === 'fr' ? 'Aucun départ programmé' : 'No scheduled departures')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredScheduled.map(shipment => (
                <Card key={shipment.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <Plane className="h-5 w-5 text-amber-500" />
                        <div>
                          <CardTitle className="text-lg">{shipment.name}</CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-4 flex-wrap">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                            </span>
                            {shipment.package_count > 0 && (
                              <span className="text-muted-foreground">
                                {shipment.package_count} {language === 'fr' ? 'colis' : 'packages'}
                              </span>
                            )}
                            {shipment.departure_date && (
                              <span className="flex items-center gap-1 font-medium text-amber-600">
                                <Calendar className="h-3.5 w-3.5" />
                                {new Date(shipment.departure_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                              </span>
                            )}
                            {shipment.transport_mode && (
                              <Badge variant="outline" className="text-xs">{shipment.transport_mode}</Badge>
                            )}
                          </CardDescription>
                        </div>
                      </div>
                      <Badge className={getStatusColor(shipment.status)}>
                        {statusLabels[shipment.status] || shipment.status}
                      </Badge>
                    </div>
                    {shipment.notes && (
                      <p className="text-sm text-muted-foreground mt-2 pl-8">{shipment.notes}</p>
                    )}
                  </CardHeader>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Colis Non Collectés */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Colis Non Collectés' : 'Uncollected Packages'}</CardTitle>
                <CardDescription>
                  {language === 'fr' ? "Colis en attente de collecte par l'administrateur" : 'Packages waiting for admin collection'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {filteredPending.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p>{searchQuery
                        ? (language === 'fr' ? 'Aucun résultat' : 'No results')
                        : (language === 'fr' ? 'Aucun colis en attente' : 'No pending packages')}</p>
                      {!searchQuery && pendingPackages.length === 0 && (
                        <p className="text-sm text-green-600 mt-2">
                          {language === 'fr' ? 'Tous vos colis ont été collectés !' : 'All your packages have been collected!'}
                        </p>
                      )}
                    </div>
                  ) : (
                    filteredPending.map(pkg => {
                      const isOld = isOldPackage(pkg);
                      return (
                        <div
                          key={pkg.id}
                          className={`p-4 border rounded-lg hover:bg-muted/30 ${isOld ? 'border-l-4 border-l-amber-500 bg-amber-50/50' : ''}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-accent">{pkg.oce_code}</p>
                                {isOld && (
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">
                                    {language === 'fr' ? 'En attente depuis +7 jours' : 'Waiting 7+ days'}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {pkg.sender_name} → {pkg.recipient_name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                                <CountryRoute origin={pkg.origin_country} destination={pkg.destination_country} size="sm" />
                                <span>| {pkg.weight} kg</span>
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>
                              <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {language === 'fr' ? 'Enregistré le' : 'Registered on'} {new Date(pkg.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                              </p>
                            </div>
                            <PackageStatusBadge status={pkg.status} size="sm" />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Historique — fully-delivered or cancelled departures */}
          <TabsContent value="history" className="space-y-4">
            {filteredHistory.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Ship className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {searchQuery
                        ? (language === 'fr' ? 'Aucun résultat' : 'No results')
                        : (language === 'fr' ? 'Aucun départ dans l\'historique' : 'No departures in history')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredHistory.map(renderShipmentCard)
            )}
          </TabsContent>
        </Tabs>
      </div>
    </ProfessionalLayout>
  );
};

export default PartnerShipmentsView;
